/**
 * Header Component - Navigation and User Profile
 * Extracted from App.js during refactoring
 * Uses React Router for navigation
 * 
 * Feature flags control visibility of:
 * - Leaderboard button (leaderboard_enabled)
 * - Explore Map button (maps_enabled)
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useUI } from '../../hooks/useUI';
import { useGamification } from '../../hooks/useGamification';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import * as api from '../../services/apiService';
import { BACKEND_URL } from '../../utils/constants';
import TeamHierarchyModal from '../TeamHierarchyModal';
import TeamActivitySummary from '../TeamActivitySummary';
import UserMapModal from '../UserMapModal';
import { useProtectedFeature } from '../../hooks/useProtectedFeature';
import ProtectedFeatureButton from '../auth/ProtectedFeatureButton';

const Header = ({
  offlineQueue,
  isProcessingOffline,
  processOfflineQueue,
  isFireExtinguisherCameraOpen,
  setIsFireExtinguisherCameraOpen
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const { instance, accounts } = useMsal();
  
  // Check if we're on admin pages
  const isAdminPage = location.pathname.startsWith('/admin');
  
  // Auth state
  const {
    currentUser,
    submittedOnBehalfOf,
    userProfile,
    showProfileDropdown,
    setShowProfileDropdown,
    setShowProfileModal,
    setShowAttributionModal,
    setAuthModalMode,
    logoutUser,
    getFirstNameFromEmail
  } = useAuth();
  
  // UI state
  const {
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    soundEnabled,
    soundVolume,
    toggleSound,
    setVolume
  } = useUI();
  
  // Gamification
  const {
    getUserTotalPoints,
    getUserRank
  } = useGamification();

  // Sales leader notifications (RSL/ASM)
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifError, setNotifError] = useState(null);
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);
  const [notifItems, setNotifItems] = useState([]);
  const [isSalesLeader, setIsSalesLeader] = useState(false);
  const [showTeamActivity, setShowTeamActivity] = useState(false);
  
  // 🌳 Team Hierarchy Modal state
  const [showTeamHierarchyModal, setShowTeamHierarchyModal] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);
  
  // 🎛️ Feature flags for conditional rendering
  const { isLeaderboardEnabled, isMapsEnabled } = useFeatureFlags();
  const { isAuthenticated } = useProtectedFeature();

  const resolveNotificationEmail = () => {
    // Admin pages: MSAL account email is the source of truth
    if (isAdminPage) {
      const adminEmail = (accounts?.[0]?.username || '').trim().toLowerCase();
      if (adminEmail.includes('@')) return adminEmail;
    }

    // User-facing pages: prefer attributed email / profile email
    const candidate =
      (submittedOnBehalfOf && submittedOnBehalfOf.includes('@') ? submittedOnBehalfOf : '') ||
      (userProfile?.email && userProfile.email.includes('@') ? userProfile.email : '') ||
      (currentUser && currentUser.includes('@') ? currentUser : '');
    
    const resolved = (candidate || '').trim().toLowerCase();
    
    // Fallback: Try localStorage directly
    if (!resolved && typeof window !== 'undefined') {
      const stored = localStorage.getItem('submittedOnBehalfOf') || '';
      if (stored.includes('@')) {
        return stored.trim().toLowerCase();
      }
    }
    
    return resolved;
  };

  const refreshNotifications = async () => {
    const email = resolveNotificationEmail();
    if (!email) return;

    setNotifLoading(true);
    setNotifError(null);
    try {
      let data;
      if (accounts?.length > 0) {
        try {
          const tokenResp = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
          });
          data = await api.fetchSalesLeaderNotifications(email, { accessToken: tokenResp?.accessToken });
        } catch (_silentErr) {
          data = await api.fetchSalesLeaderNotifications(email);
        }
      } else if (localStorage.getItem('token')) {
        data = await api.fetchSalesLeaderNotifications(email);
      } else {
        setNotifLoading(false);
        return;
      }
      const unread = Number(data?.unread_count || 0);
      const list = Array.isArray(data?.notifications) ? data.notifications : [];
      setNotifUnreadCount(Number.isFinite(unread) ? unread : 0);
      setNotifItems(list);
      setIsSalesLeader(!!data?.officer);
    } catch (e) {
      // Highest-integrity behavior: don't break the UI if auth isn't allowed.
      setNotifError(e?.message || 'Failed to load team activity');
      setNotifUnreadCount(0);
      setNotifItems([]);
      setIsSalesLeader(false);
    } finally {
      setNotifLoading(false);
    }
  };
  
  // Determine active page from router location
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  // Close dropdown when clicking outside (but not when auth modal is open)
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if click is inside an auth modal (check DOM tree for data-auth-modal attribute)
      const clickedElement = event.target;
      const isInsideAuthModal = clickedElement.closest('[data-auth-modal="true"]');
      
      if (isInsideAuthModal) {
        console.log('🔐 Click inside auth modal - keeping dropdown open');
        return;
      }
      
      // Don't close if global flag is set (backup check)
      if (window.__authModalOpen) {
        console.log('🔐 Auth modal open (global flag) - keeping dropdown open');
        return;
      }
      
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };
    
    if (showProfileDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProfileDropdown, setShowProfileDropdown]);

  // Poll notifications lightly (only on user-facing pages and only if leader)
  useEffect(() => {
    const email = resolveNotificationEmail();
    if (!email) return;

    // Initial fetch (silent)
    refreshNotifications();

    const interval = setInterval(() => {
      refreshNotifications();
    }, isAdminPage ? 30000 : 60000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminPage, accounts?.[0]?.username, submittedOnBehalfOf, userProfile?.email, currentUser]);
  
  return (
    <header 
      className="fixed top-0 left-0 right-0 z-50 bg-[var(--pbfs-navy)] backdrop-blur-md border-b border-white/10"
      style={{paddingTop: 'env(safe-area-inset-top, 0px)'}}
    >
      <div className="px-2 sm:px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between gap-2">
          
          {/* Logo - Responsive sizing */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 relative flex-shrink-0">
            <img 
              src="/images/PBFS-white.png" 
              alt="PBFS Logo" 
              className="h-10 sm:h-12 w-auto"
            />
            <div className="hidden md:block">
              <div className="flex items-center gap-2">
                <div className="text-white font-bold text-lg font-loos tracking-tight">TagQuest</div>
                <div className="text-white/30 text-xs font-mono">v92</div>
              </div>
              <div className="text-white/60 text-xs font-verdana">Fire Safety Scanner</div>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-2">
            {/* All navigation moved to user dropdowns and admin portal */}
          </nav>
          
          {/* Right Side Actions */}
          <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
            
            {/* Offline Queue Indicator */}
            {offlineQueue.length > 0 && (
              <button
                onClick={processOfflineQueue}
                disabled={isProcessingOffline}
                className={`px-3 py-2 rounded-lg border transition-all duration-300 ${
                  isProcessingOffline
                    ? 'bg-blue-500/20 border-blue-500/50 text-blue-400 cursor-wait'
                    : 'bg-orange-500/20 border-orange-500/50 text-orange-400 hover:bg-orange-500/30'
                }`}
                title={`${offlineQueue.length} submissions queued offline`}
              >
                <div className="flex items-center space-x-2">
                  {isProcessingOffline ? (
                    <>
                      <div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
                      <span className="text-sm font-medium hidden sm:inline">Syncing...</span>
                    </>
                  ) : (
                    <>
                      <span>📡</span>
                      <span className="text-sm font-medium hidden sm:inline">{offlineQueue.length} Queued</span>
                    </>
                  )}
                </div>
              </button>
            )}
            
            {/* Admin User Button (on admin pages) */}
            {isAdminPage && accounts.length > 0 ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => {
                    const next = !showProfileDropdown;
                    setShowProfileDropdown(next);
                    if (next) {
                      refreshNotifications();
                      setShowTeamActivity(false);
                    }
                  }}
                  className="flex items-center space-x-2 px-3 py-2 bg-[var(--pbfs-navy)]/60 hover:bg-[var(--pbfs-navy)]/80 rounded-lg transition-all duration-300 border border-[var(--pbfs-green)]/30"
                >
                  <div className="w-8 h-8 bg-gradient-to-r from-[var(--pbfs-green)] to-[var(--pbfs-blue)] rounded-full flex items-center justify-center relative">
                    <span className="text-white font-bold text-sm">
                      {accounts[0]?.name?.charAt(0).toUpperCase() || 'A'}
                    </span>
                    {/* Sales leader notification badge */}
                    {isSalesLeader && notifUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[var(--pbfs-green)] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border border-[var(--pbfs-navy)]">
                        {notifUnreadCount > 99 ? '99+' : notifUnreadCount}
                      </span>
                    )}
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-white text-sm font-medium">
                      {accounts[0]?.name || accounts[0]?.username || 'Admin'}
                    </div>
                    <div className="text-[var(--pbfs-green)] text-xs font-bold">
                      Administrator
                    </div>
                  </div>
                  <span className="text-white/60 text-xs">
                    {showProfileDropdown ? '▲' : '▼'}
                  </span>
                </button>
                
                {/* Admin Dropdown */}
                {showProfileDropdown && (
                  <div className="absolute right-0 mt-2 w-56 bg-[var(--pbfs-navy)] border border-[var(--pbfs-green)]/30 rounded-lg shadow-xl z-50">
                    <div className="p-4 border-b border-white/10">
                      <p className="text-white font-bold">Admin Access</p>
                      <p className="text-white/70 text-sm mt-1">
                        {accounts[0]?.username || 'Authenticated'}
                      </p>
                    </div>

                    {/* 🌳 Team Activity Summary (Admin - RSL / ASM) */}
                    {isSalesLeader && (
                      <div className="p-3 border-b border-white/10">
                        <TeamActivitySummary
                          submissionCount={notifUnreadCount}
                          days={7}
                          loading={notifLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('🌳 Opening Team Hierarchy Modal (Admin)');
                            setShowTeamHierarchyModal(true);
                            setShowProfileDropdown(false);  // Close dropdown when modal opens
                          }}
                        />
                      </div>
                    )}

                    <button
                      onClick={() => {
                        instance.logoutRedirect();
                        navigate('/admin');
                      }}
                      className="w-full text-left px-4 py-3 text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : null}
            
            {/* Front-end User Profile (non-admin pages) */}
            {!isAdminPage && currentUser && currentUser !== 'Not logged in' && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => {
                    const next = !showProfileDropdown;
                    setShowProfileDropdown(next);
                    // Fetch on open to keep it fresh
                    if (next) {
                      refreshNotifications();
                      setShowTeamActivity(false);
                    }
                  }}
                  className="flex items-center space-x-1.5 sm:space-x-2 px-2 sm:px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all duration-300 border border-white/20"
                >
                  <div className="w-8 h-8 bg-pb-green rounded-full flex items-center justify-center relative flex-shrink-0">
                    <span className="text-white font-bold text-sm">
                      {currentUser.charAt(0).toUpperCase()}
                    </span>
                    {/* Notification badge (sales leaders only) */}
                    {isSalesLeader && notifUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[var(--pbfs-green)] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center border border-[var(--pbfs-navy)]">
                        {notifUnreadCount > 99 ? '99+' : notifUnreadCount}
                      </span>
                    )}
                  </div>
                  {/* Mobile & Desktop: Show points next to initial */}
                  <div className="text-left flex-shrink-0">
                    <div className="text-white text-sm font-medium hidden sm:block">{getFirstNameFromEmail(currentUser)}</div>
                    <div className="text-pb-green text-xs sm:text-sm font-bold font-eurostile tracking-wider">
                      {getUserTotalPoints()}
                    </div>
                  </div>
                  <span className="text-white/60 text-xs hidden sm:inline flex-shrink-0">
                    {showProfileDropdown ? '▲' : '▼'}
                  </span>
                </button>
                
                {/* Profile Dropdown - Enterprise/Apple Style */}
                {showProfileDropdown && (
                  <div 
                    className="absolute right-0 mt-3 w-[min(90vw,320px)] sm:w-80 bg-[var(--pbfs-navy)] border border-white/10 rounded-2xl shadow-2xl overflow-hidden z-50 ring-1 ring-white/5 animate-scaleIn origin-top-right"
                  >
                    {/* Gamification Header - User Info */}
                    <div className="px-4 py-4 bg-gradient-to-b from-white/5 to-transparent border-b border-white/10">
                      
                      {/* Level & Title Logic */}
                      {(() => {
                        const totalPoints = getUserTotalPoints();
                        const levels = [
                          { level: 1, title: 'Safety Apprentice', points: 0, emoji: '👷' },
                          { level: 2, title: 'Fire Fighter', points: 3, emoji: '🚒' },
                          { level: 3, title: 'Blaze Buster', points: 9, emoji: '🔥' },
                          { level: 4, title: 'Inferno Inspector', points: 18, emoji: '💪' },
                          { level: 5, title: 'Flame Master', points: 30, emoji: '🏆' },
                          { level: 6, title: 'Fire Marshal', points: 45, emoji: '⭐' },
                          { level: 7, title: 'Safety Legend', points: 63, emoji: '👑' },
                          { level: 8, title: 'Guardian Supreme', points: 84, emoji: '🌟' }
                        ];
                        
                        let currentLevel = levels[0];
                        for (let i = levels.length - 1; i >= 0; i--) {
                          if (totalPoints >= levels[i].points) {
                            currentLevel = levels[i];
                            break;
                          }
                        }
                        
                        const nextLevel = levels[currentLevel.level] || currentLevel;
                        const pointsNeededForLevel = nextLevel.points - currentLevel.points;
                        const pointsInCurrentLevel = totalPoints - currentLevel.points;
                        const pointsToNextLevel = nextLevel.points - totalPoints;
                        const userRank = getUserRank();
                        
                        // Fix for max level division by zero
                        const progressPercentage = pointsNeededForLevel > 0 
                          ? (pointsInCurrentLevel / pointsNeededForLevel) * 100 
                          : 100;
                        
                        return (
                          <>
                            {/* User Profile Card */}
                            <div className="flex items-center gap-4 mb-5">
                               <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--pbfs-green)] to-[#008f35] flex items-center justify-center text-2xl shadow-[0_4px_12px_rgba(13,177,75,0.3)] border border-white/20">
                                  {currentLevel.emoji}
                               </div>
                               <div>
                                  <div className="text-white font-bold text-lg leading-tight tracking-tight">{getFirstNameFromEmail(currentUser)}</div>
                                  <div className="text-[var(--pbfs-green)] text-xs font-bold tracking-wider uppercase mt-0.5">{currentLevel.title} • Lvl {currentLevel.level}</div>
                               </div>
                            </div>
                            
                            {/* Quick Stats - Grid Layout (Clickable to Leaderboard if enabled) */}
                            <div className="grid grid-cols-2 gap-2 mb-4">
                              <button
                                onClick={() => {
                                  if (isLeaderboardEnabled) {
                                    setShowProfileDropdown(false);
                                    navigate('/leaderboard');
                                  }
                                }}
                                className={`bg-white/5 rounded-xl p-2.5 border border-white/5 flex flex-col items-center justify-center transition-all group ${
                                  isLeaderboardEnabled 
                                    ? 'hover:bg-white/10 hover:border-[var(--pbfs-green)]/30 cursor-pointer active:scale-95' 
                                    : 'cursor-default'
                                }`}
                                title={isLeaderboardEnabled ? "View full leaderboard" : "Points earned"}
                              >
                                  <span className="text-white/40 text-[10px] uppercase tracking-wider font-semibold group-hover:text-white/60 transition-colors">Points</span>
                                  <span className="text-white font-bold text-2xl font-eurostile drop-shadow-md">{totalPoints}</span>
                              </button>
                              <button
                                onClick={() => {
                                  if (isLeaderboardEnabled) {
                                    setShowProfileDropdown(false);
                                    navigate('/leaderboard');
                                  }
                                }}
                                className={`bg-white/5 rounded-xl p-2.5 border border-white/5 flex flex-col items-center justify-center transition-all group ${
                                  isLeaderboardEnabled 
                                    ? 'hover:bg-white/10 hover:border-[var(--pbfs-green)]/30 cursor-pointer active:scale-95' 
                                    : 'cursor-default'
                                }`}
                                title={isLeaderboardEnabled ? "View full leaderboard" : "Your current rank"}
                              >
                                  <span className="text-white/40 text-[10px] uppercase tracking-wider font-semibold group-hover:text-white/60 transition-colors">Rank</span>
                                  <div className="flex items-center gap-1">
                                    <span className="text-[var(--pbfs-gray)] text-lg">#</span>
                                    <span className="text-white font-bold text-2xl font-eurostile drop-shadow-md">{userRank || '?'}</span>
                                  </div>
                              </button>
                            </div>
                            
                            {/* Progress Bar - Minimalist */}
                            <div className="mb-1">
                              <div className="flex justify-between text-[10px] font-medium text-white/40 mb-1.5 px-0.5">
                                <span>Level Progress</span>
                                <span>{Math.round(progressPercentage)}%</span>
                              </div>
                              <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden shadow-inner border border-white/5">
                                <div 
                                  className="h-full bg-gradient-to-r from-[var(--pbfs-green)] to-[#2ecc71] shadow-[0_0_10px_rgba(46,204,113,0.5)] transition-all duration-700 ease-out"
                                  style={{ width: `${Math.min(100, progressPercentage)}%` }}
                                />
                              </div>
                              <p className="text-center text-white/30 text-[10px] mt-2 font-medium">
                                {currentLevel.level < 8 ? `${pointsToNextLevel} pts to next level` : 'Max level achieved!'}
                              </p>
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* 🌳 Team Activity Summary (User - RSL / ASM) */}
                    {isSalesLeader && (
                      <div className="p-2 border-b border-white/10 bg-white/[0.02]">
                        <TeamActivitySummary
                          submissionCount={notifUnreadCount}
                          days={7}
                          loading={notifLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            console.log('🌳 Opening Team Hierarchy Modal (User)');
                            setShowTeamHierarchyModal(true);
                            setShowProfileDropdown(false);
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Action Grid - Leaderboard & Map - HIDDEN (use bottom nav instead) */}
                    {/* Removed redundant buttons - bottom nav works perfectly */}

                    {/* Footer Actions */}
                    <div className="p-2 space-y-1">
                       {/* Feedback Row */}
                      <a
                        href="https://app.smartsheet.com/b/form/019be726f50874f29f10af7b58880a52"
                        target="_blank"
                        rel="noopener noreferrer"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          setTimeout(() => setShowProfileDropdown(false), 10);
                        }}
                        className="flex items-center w-full px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/5 rounded-lg text-sm transition-all font-medium group"
                      >
                        <span className="w-6 text-center mr-2 text-base group-hover:scale-110 transition-transform">💬</span>
                        Feedback
                      </a>

                      {/* Settings Row */}
                      <details 
                        className="group/settings" 
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <summary 
                          className="flex items-center w-full px-3 py-2.5 text-white/60 hover:text-white hover:bg-white/5 rounded-lg text-sm transition-all font-medium cursor-pointer list-none select-none"
                        >
                          <span className="w-6 text-center mr-2 text-base group-hover/settings:rotate-90 transition-transform duration-300">⚙️</span>
                          <span className="flex-1">Settings</span>
                          <span className="text-[10px] text-white/20 group-open/settings:rotate-180 transition-transform">▼</span>
                        </summary>
                        
                        <div className="mt-1 mb-1 ml-2 pl-3 border-l border-white/10 space-y-1">
                          {/* How to Play */}
                          <details className="group/play">
                            <summary className="text-[var(--pbfs-green)]/80 hover:text-[var(--pbfs-green)] text-xs px-2 py-2 cursor-pointer hover:bg-white/5 rounded-lg transition-colors list-none font-medium">
                              How to Play?
                            </summary>
                            <div className="px-2 py-2 text-white/50 text-[11px] space-y-1.5 leading-relaxed">
                              <p>• Scan tags = 1 point</p>
                              <p>• Level up every 3 scans</p>
                              <p>• Earn badges for streaks</p>
                            </div>
                          </details>
                          
                          {/* Sound Toggle */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleSound();
                            }}
                            className="w-full flex items-center justify-between px-2 py-2 hover:bg-white/5 rounded-lg transition-colors text-xs group/sound"
                          >
                            <span className="text-white/60 group-hover/sound:text-white/80 transition-colors">Sounds</span>
                            <div className={`relative w-9 h-5 rounded-full transition-colors duration-300 ${soundEnabled ? 'bg-[var(--pbfs-green)]' : 'bg-white/20'}`}>
                              <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-md transition-all duration-300 ${soundEnabled ? 'left-[18px]' : 'left-0.5'}`}></div>
                            </div>
                          </button>
                          
                          {/* Edit Profile */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowProfileModal(true);
                              setShowProfileDropdown(false);
                            }}
                            className="w-full text-left px-2 py-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg text-xs transition-colors"
                          >
                            Edit Profile
                          </button>
                          
                          {/* Privacy Policy - uses Capacitor Browser on native to avoid Safari link interception */}
                          <button
                            className="block w-full text-left px-2 py-2 text-white/50 hover:text-white hover:bg-white/5 rounded-lg text-xs transition-colors"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { Capacitor } = await import('@capacitor/core');
                                if (Capacitor.isNativePlatform()) {
                                  const { Browser } = await import('@capacitor/browser');
                                  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://tagquest.pyebarkerfs.com';
                                  await Browser.open({ url: `${backendUrl}/privacy.html`, presentationStyle: 'popover' });
                                  return;
                                }
                              } catch (_) { /* web build fallback */ }
                              window.open('/privacy.html', '_blank', 'noopener,noreferrer');
                            }}
                          >
                            Privacy Policy
                          </button>
                          
                          {/* Logout */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              logoutUser();
                              setShowProfileDropdown(false);
                            }}
                            className="w-full text-left px-2 py-2 text-red-400/70 hover:text-red-400 hover:bg-red-400/10 rounded-lg text-xs transition-colors"
                          >
                            Log Out
                          </button>
                        </div>
                      </details>
                    </div>
                  </div>
                )}
              </div>
            )
            /* Not logged in - No user icon shown. User must complete onboarding first. */
            }
            
            {/* Mobile Menu Toggle - REMOVED */}
          </div>
        </div>
      </div>
      
      {/* 🌳 Team Hierarchy Modal - Opens when clicking Team Activity Summary */}
      <TeamHierarchyModal
        isOpen={showTeamHierarchyModal}
        onClose={() => setShowTeamHierarchyModal(false)}
        userEmail={resolveNotificationEmail()}
        backendUrl={BACKEND_URL}
        days={7}
      />

      {/* 🗺️ User Map Modal - Opens when clicking My Tag Map */}
      <UserMapModal
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
      />
    </header>
  );
};

export default Header;

// Custom CSS for volume slider
const styles = `
  .slider-thumb-green::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--pbfs-green);
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  }
  
  .slider-thumb-green::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--pbfs-green);
    cursor: pointer;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('volume-slider-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'volume-slider-styles';
    style.textContent = styles;
    document.head.appendChild(style);
  }
}
